import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerShell from '../../components/customer/CustomerShell';
import { useCustomerAuth } from '../../App';
import { customerApi } from '../../lib/api';
import { useWalletBalance } from '../../hooks/useWalletQueries';
import { formatCurrency, formatDateTime, getPaymentStatusClass, PAYMENT_STATUS_LABELS, CHECK_IN_STATUS_LABELS, getCheckInStatusClass } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { CalendarDays, Ticket, Star, ChevronRight, Zap, ArrowRight, Wallet, Hourglass, AlertTriangle } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';

function EventCard({ event, onClick }) {
  const hasCharge = event.cover_charge > 0;
  return (
    <div
      onClick={onClick}
      className="surface-card card-highlight rounded-xl overflow-hidden cursor-pointer hover:border-[#D4A843]/40 transition-all border border-[#2A2A2A] group active:scale-[0.98]"
      data-testid="event-card"
    >
      {event.banner_image && (
        <div className="h-28 sm:h-32 overflow-hidden relative">
          <img src={event.banner_image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          {event.booking_open ? (
            <span className="absolute top-2 right-2 text-[10px] bg-[#D4A843] text-[#120F08] px-2.5 py-1 rounded-full font-semibold shadow-lg">Open</span>
          ) : (
            <span className="absolute top-2 right-2 text-[10px] bg-[#2A2A2A] text-[#B9B9B9] px-2.5 py-1 rounded-full shadow-lg">Closed</span>
          )}
        </div>
      )}
      <div className="p-3 sm:p-3.5">
        <p className="text-[#F2F2F2] font-semibold text-sm sm:text-base mb-1 line-clamp-1">{event.title}</p>
        <p className="text-[#B9B9B9] text-xs sm:text-sm mb-2">{event.start_date}{event.event_time ? ` · ${event.event_time}` : ''}</p>
        {event.venue_name && <p className="text-[#6F6F6F] text-xs mb-2 line-clamp-1">{event.venue_name}</p>}
        <div className="flex items-center justify-between">
          <span className="text-sm sm:text-base font-['EB_Garamond'] text-[#D4A843] font-semibold">
            {hasCharge ? formatCurrency(event.cover_charge) : 'Free Entry'}
          </span>
          <ChevronRight size={14} className="text-[#6F6F6F] group-hover:text-[#D4A843] transition-colors" strokeWidth={2.5} />
        </div>
      </div>
    </div>
  );
}

export default function AccountDashboard() {
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { data: walletBalance } = useWalletBalance();

  useEffect(() => {
    Promise.all([
      customerApi.getEvents({ limit: 4 }),
      customerApi.getBookings({ limit: 3 }),
    ]).then(([evRes, bkRes]) => {
      setEvents(evRes.data.events || []);
      setBookings(bkRes.data.bookings || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const firstName = customer?.full_name?.split(' ')[0] || 'Guest';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <CustomerShell>
      <div className="space-y-5 sm:space-y-6">
        {/* Welcome hero - mobile optimized */}
        <div 
          className="hero-gradient rounded-2xl p-4 sm:p-6 border border-[#2A2A2A]" 
          style={{ background: 'radial-gradient(800px circle at 10% 50%, rgba(212,168,67,0.13), rgba(10,10,10,0) 55%), linear-gradient(135deg,#111111,#0A0A0A)' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[#B9B9B9] text-sm mb-1">{greeting}</p>
              <h1 className="font-['EB_Garamond'] text-2xl sm:text-3xl text-[#F2F2F2] mb-2 truncate">{firstName}</h1>
              <p className="text-[#B9B9B9] text-sm">Your exclusive lounge experience awaits.</p>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center flex-shrink-0">
              <span className="text-[#120F08] font-bold text-lg sm:text-xl">{firstName[0]}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex flex-col sm:flex-row gap-2.5 sm:gap-3">
            <Button 
              size="sm" 
              className="btn-gold rounded-lg gap-2 text-sm h-10 sm:h-9 w-full sm:w-auto font-medium active:scale-95 transition-transform" 
              onClick={() => navigate('/account/events')}
            >
              <CalendarDays size={16} /> View Events
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="rounded-lg gap-2 text-sm h-10 sm:h-9 w-full sm:w-auto font-medium bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 active:scale-95 transition-all" 
              onClick={() => navigate('/checkin')}
            >
              <Zap size={16} /> Quick Check-in
            </Button>
          </div>
        </div>

        {/* Scratch Points Wallet Card */}
        <div
          data-testid="dashboard-wallet-card"
          onClick={() => navigate('/account/wallet')}
          className="rounded-2xl p-5 border cursor-pointer hover:border-[#D4A843]/60 transition-all active:scale-[0.99]"
          style={{
            background: 'linear-gradient(135deg, rgba(212,168,67,0.10) 0%, rgba(212,168,67,0.04) 100%)',
            borderColor: walletBalance?.expiring_soon ? 'rgba(255,77,77,0.30)' : 'rgba(212,168,67,0.30)',
          }}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(212,168,67,0.16)' }}
              >
                <Wallet size={22} className="text-[#D4A843]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[#B9B9B9] text-xs uppercase tracking-widest mb-1">Scratch Points</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold font-mono text-[#F2F2F2]">
                    {walletBalance?.available ?? 0}
                  </p>
                  <p className="text-xs text-[#6B6B6B]">≈ ₹{walletBalance?.available ?? 0}</p>
                </div>
                {walletBalance?.expiring_soon > 0 && walletBalance?.next_expiry_at && (
                  <p className="text-xs text-[#FF8A8A] mt-1 flex items-center gap-1">
                    <AlertTriangle size={11} />
                    {walletBalance.expiring_soon} pts expiring {formatDistanceToNowStrict(new Date(walletBalance.next_expiry_at), { addSuffix: true })}
                  </p>
                )}
                {walletBalance?.expiring > 0 && !walletBalance?.expiring_soon && (
                  <p className="text-xs text-[#B9B9B9] mt-1 flex items-center gap-1">
                    <Hourglass size={11} />
                    {walletBalance.expiring} expiring within 12h
                  </p>
                )}
              </div>
            </div>
            <ChevronRight size={20} className="text-[#D4A843] flex-shrink-0" />
          </div>
        </div>

        {/* Upcoming events */}
        <div>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="font-['EB_Garamond'] text-xl sm:text-2xl text-[#F2F2F2] font-semibold">Upcoming Events</h2>
            <button 
              onClick={() => navigate('/account/events')} 
              className="text-xs sm:text-sm text-[#D4A843] hover:underline flex items-center gap-1 font-medium active:scale-95 transition-transform"
            >
              See all <ArrowRight size={12} />
            </button>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[0, 1].map(i => <div key={i} className="h-48 bg-[#1A1A1A] rounded-xl animate-pulse" />)}
            </div>
          ) : events.length === 0 ? (
            <div className="surface-card rounded-xl p-6 text-center">
              <CalendarDays size={28} className="text-[#D4A843] mx-auto mb-2" />
              <p className="text-[#B9B9B9] text-sm">No upcoming events right now</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {events.map(ev => (
                <EventCard key={ev._id} event={ev} onClick={() => navigate(`/account/events?event=${ev.slug}`)} />
              ))}
            </div>
          )}
        </div>

        {/* Recent bookings */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-['EB_Garamond'] text-xl text-[#F2F2F2]">Recent Bookings</h2>
            <button onClick={() => navigate('/account/bookings')} className="text-xs text-[#D4A843] hover:underline flex items-center gap-1">
              See all <ArrowRight size={11} />
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">{[0,1,2].map(i => <div key={i} className="h-16 bg-[#1A1A1A] rounded-xl animate-pulse" />)}</div>
          ) : bookings.length === 0 ? (
            <div className="surface-card rounded-xl p-6 text-center">
              <Ticket size={28} className="text-[#D4A843] mx-auto mb-2" />
              <p className="text-[#B9B9B9] text-sm">No bookings yet</p>
              <Button size="sm" className="btn-gold mt-3 h-8 rounded-lg text-xs gap-1" onClick={() => navigate('/checkin')}>
                <Zap size={12} /> Check In Now
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.map(bk => (
                <div key={bk._id} className="surface-card card-highlight rounded-xl p-3.5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#D4A843]/10 border border-[#D4A843]/30 flex items-center justify-center flex-shrink-0">
                    <Ticket size={15} className="text-[#D4A843]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#F2F2F2] font-medium truncate">{bk.visit_date} · {bk.guests} guest{bk.guests > 1 ? 's' : ''}</p>
                    <p className="text-xs text-[#B9B9B9] text-mono">{bk.reference_id}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getPaymentStatusClass(bk.payment_status)}`}>
                      {PAYMENT_STATUS_LABELS[bk.payment_status] || bk.payment_status}
                    </span>
                    <span className="text-xs text-[#D4A843]">{formatCurrency(bk.total_amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CustomerShell>
  );
}
